import { useState, useEffect } from "react"
import { useUpdateUserMutation, useDeleteUserMutation } from "./usersApiSlice"
import { useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave, faTrashCan } from "@fortawesome/free-solid-svg-icons"
import { ROLES } from "../../config/roles"
import { Button, Spinner, Form } from 'react-bootstrap';

const USER_REGEX = /^[A-z\s]{3,50}$/
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_REGEX = /^[6789][0-9]{9}$/;

const EditUserForm = ({ user }) => {

    const [updateUser, {
        isLoading,
        isSuccess,
        isError,
        error
    }] = useUpdateUserMutation()

    const [deleteUser, {
        isSuccess: isDelSuccess,
        isError: isDelError,
        error: delerror
    }] = useDeleteUserMutation()

    const navigate = useNavigate()

    const [username, setUsername] = useState(user.name || '')
    const [validUsername, setValidUsername] = useState(USER_REGEX.test(user.name || ''))
    const [email, setEmail] = useState(user.email || '')
    const [validEmail, setValidEmail] = useState(EMAIL_REGEX.test(user.email || ''))
    const [mobile, setMobile] = useState(user.mobile || '')
    const [validMobile, setValidMobile] = useState(MOBILE_REGEX.test(user.mobile || ''))
    const [roles, setRoles] = useState(user.role || [])
    const [active, setActive] = useState(user.userStatus === 'ACTIVE')
    const [membershipTier, setMembershipTier] = useState(user.membershipTier || 'NONE');
    const [membershipStatus, setMembershipStatus] = useState(user.membershipStatus || 'INACTIVE');
    const [isSponsoredMember, setIsSponsoredMember] = useState(user.isSponsoredMember || false);
    // ✅ नए 'ब्लॉक' फ़ील्ड्स
    const [isBlocked, setIsBlocked] = useState(user.isBlocked || false);
    const [blockedReason, setBlockedReason] = useState(user.blockedReason || '');


    useEffect(() => {
        setValidUsername(USER_REGEX.test(username))
    }, [username])

    useEffect(() => {
        setValidEmail(EMAIL_REGEX.test(email)); 
    }, [email])

    useEffect(() => {
        setValidMobile(MOBILE_REGEX.test(mobile)); 
    }, [mobile])

    useEffect(() => {
        if (isSuccess || isDelSuccess) {
            setUsername('')
            setEmail('')
            setMobile('')
            setRoles([])
            setMembershipTier('NONE');
            setMembershipStatus('INACTIVE');
            setIsSponsoredMember(false);
            // ✅ ब्लॉक फ़ील्ड्स को रीसेट करें
            setIsBlocked(false);
            setBlockedReason('');
            navigate('/dashboard/users')
        }

    }, [isSuccess, isDelSuccess, navigate])

    const onUsernameChanged = e => setUsername(e.target.value)
    const onEmailChanged = e => setEmail(e.target.value)
    const onMobileChanged = e => setMobile(e.target.value)

    const onRolesChanged = e => {
        const values = Array.from(
            e.target.selectedOptions,
            (option) => option.value
        )
        setRoles(values)
    }

    const onActiveChanged = () => setActive(prev => !prev)

    const onIsSponsoredMemberChanged = () => {
        setIsSponsoredMember(prev => !prev);
        if (!isSponsoredMember) {
            setMembershipTier('PREMIUM');
            setMembershipStatus('SPONSORED');
        } else {
            setMembershipTier('NONE');
            setMembershipStatus('INACTIVE');
        }
    };
    
    const onMembershipTierChanged = e => {
        const value = e.target.value;
        setMembershipTier(value);
        if (value !== 'NONE' && membershipStatus === 'INACTIVE') {
            setMembershipStatus('ACTIVE');
        } else if (value === 'NONE') {
            setMembershipStatus('INACTIVE');
        }
    };

    const onMembershipStatusChanged = e => {
        const value = e.target.value;
        setMembershipStatus(value);
        if (value === 'SPONSORED') {
            setIsSponsoredMember(true);
            setMembershipTier('PREMIUM');
        } else if (value !== 'SPONSORED' && isSponsoredMember) {
            setIsSponsoredMember(false);
        }
    };

    // ✅ isBlocked को टॉगल करने के लिए नया हैंडलर
    const onIsBlockedChanged = () => {
        setIsBlocked(prev => !prev);
        // जब उपयोगकर्ता अनब्लॉक होता है, तो कारण साफ कर दें
        if (isBlocked) { // यदि यह 'false' होने जा रहा है
            setBlockedReason('');
        }
    };
    // ✅ blockedReason को बदलने के लिए नया हैंडलर
    const onBlockedReasonChanged = e => setBlockedReason(e.target.value);


    const onSaveUserClicked = async (e) => {
        e.preventDefault();
        let userDataToUpdate = {
            id: user.id,
            name: username,
            email,
            mobile,
            roles,
            userStatus: active ? 'ACTIVE' : 'INACTIVE',
            membershipTier,
            membershipStatus,
            isSponsoredMember,
            isBlocked,      // ✅ isBlocked शामिल करें
            blockedReason: isBlocked ? blockedReason : undefined, // ✅ blockedReason शामिल करें, यदि ब्लॉक नहीं है तो undefined
        };
        
        try {
            await updateUser(userDataToUpdate).unwrap();
        } catch (err) {
            console.error('Failed to save user:', err);
        }
    }


    const onDeleteUserClicked = async () => {
        await deleteUser({ id: user.id })
    }

    const rolesOptions = Object.values(ROLES).map(role => {
        return (
            <option
                key={role}
                value={role}
            > {role}</option >
        )
    })

    const membershipTierOptions = ["NONE", "BASIC", "PREMIUM"].map(tier => {
        return (
            <option key={tier} value={tier}>
                {tier === 'NONE' ? 'कोई नहीं' : tier === 'BASIC' ? 'बुनियादी' : 'प्रीमियम'}
            </option>
        );
    });

    const membershipStatusOptions = ["ACTIVE", "INACTIVE", "PENDING", "EXPIRED", "CANCELLED", "FREE_TRIAL", "SPONSORED"].map(status => {
        return (
            <option key={status} value={status}>
                {status === 'ACTIVE' ? 'सक्रिय' :
                 status === 'INACTIVE' ? 'निष्क्रिय' :
                 status === 'PENDING' ? 'लंबित' :
                 status === 'EXPIRED' ? 'समाप्त' :
                 status === 'CANCELLED' ? 'रद्द' :
                 status === 'FREE_TRIAL' ? 'मुफ्त परीक्षण' :
                 status === 'SPONSORED' ? 'प्रायोजित' : status}
            </option>
        );
    });


    // ✅ canSave लॉजिक अपडेटेड
    const baseValid = [
        validUsername,
        validEmail,
        validMobile,
        Boolean(roles.length) 
    ].every(Boolean);

    let canSave = baseValid && !isLoading;

    // यदि उपयोगकर्ता ब्लॉक है, तो blockedReason अनिवार्य है
    const isBlockedReasonRequired = isBlocked && (!blockedReason || blockedReason.trim() === '');
    if (isBlockedReasonRequired) {
        canSave = false; // यदि ब्लॉक किया गया है और कारण खाली है तो सहेज नहीं सकते
    }

    // जांचें कि क्या कोई बदलाव नहीं हुआ है ताकि अनावश्यक बचत को रोका जा सके
    const noChanges = username === (user.name || '') &&
                     email === (user.email || '') &&
                     mobile === (user.mobile || '') &&
                     JSON.stringify(roles.sort()) === JSON.stringify(user.role.sort()) &&
                     active === (user.userStatus === 'ACTIVE') &&
                     membershipTier === (user.membershipTier || 'NONE') &&
                     membershipStatus === (user.membershipStatus || 'INACTIVE') &&
                     isSponsoredMember === (user.isSponsoredMember || false) &&
                     isBlocked === (user.isBlocked || false) && // ✅ isBlocked तुलना करें
                     blockedReason === (user.blockedReason || ''); // ✅ blockedReason तुलना करें
                     

    canSave = canSave && !noChanges;

    // ✅ डीबगिंग लॉग्स
    console.log("--- EditUserForm Debug ---");
    console.log("username:", username, "validUsername:", validUsername);
    console.log("email:", email, "validEmail:", validEmail);
    console.log("mobile:", mobile, "validMobile:", validMobile);
    console.log("roles:", roles, "Boolean(roles.length):", Boolean(roles.length));
    console.log("baseValid:", baseValid);
    console.log("isLoading:", isLoading);
    console.log("isBlocked:", isBlocked, "blockedReason:", blockedReason);
    console.log("isBlockedReasonRequired:", isBlockedReasonRequired);
    console.log("noChanges:", noChanges);
    console.log("Final canSave:", canSave);
    console.log("------------------------");


    const errClass = (isError || isDelError) ? "errmsg" : "offscreen"
    const validUserClass = !validUsername ? 'form__input--incomplete' : ''
    const validEmailClass = !validEmail ? 'form__input--incomplete' : ''
    const validMobileClass = !validMobile ? 'form__input--incomplete' : ''
    const validRolesClass = !Boolean(roles.length) ? 'form__input--incomplete' : ''
    const validBlockedReasonClass = isBlocked && (!blockedReason || blockedReason.trim() === '') ? 'form__input--incomplete' : ''; // ✅ नया

    const errContent = (error?.data?.message || delerror?.data?.message) ?? ''


    const content = (
        <><div className="container">
            <div className="row justify-content-md-center">
            <h2 className="pageHeading">Edit User</h2>
            
            <p className={`${errClass} error`}>{errContent}</p>

            <Form className="form" onSubmit={onSaveUserClicked}>
                <div className="form__title-row">
                    
                    <div className="form__action-buttons">
                        <Button
                            className="icon-button"
                            title="Save"
                            onClick={onSaveUserClicked}
                            disabled={!canSave}
                            variant="primary"
                        >
                            <FontAwesomeIcon icon={faSave} /> Save {isLoading ? (<Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />) : null}
                        </Button>
                        <Button
                            className="icon-button"
                            title="Delete"
                            onClick={onDeleteUserClicked}
                            variant="danger"
                        >
                            <FontAwesomeIcon icon={faTrashCan} /> Delete
                        </Button>
                    </div>
                </div>
                <Form.Group className="formGroup">
                    <Form.Label htmlFor="username">
                        Name: <span className="nowrap">[3-50 letters and spaces]</span>
                    </Form.Label>
                    <Form.Control
                        className={`${validUserClass}`}
                        id="username"
                        name="username"
                        type="text"
                        autoComplete="off"
                        value={username}
                        onChange={onUsernameChanged}
                        required
                    />
                </Form.Group>
                
                <Form.Group className="formGroup">
                    <Form.Label htmlFor="email">
                        Email:
                    </Form.Label>
                    <Form.Control
                        className={`${validEmailClass}`}
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="off"
                        value={email}
                        onChange={onEmailChanged}
                        required
                    />
                </Form.Group>

                <Form.Group className="formGroup">
                    <Form.Label htmlFor="mobile">
                        Mobile:
                    </Form.Label>
                    <Form.Control
                        className={`${validMobileClass}`}
                        id="mobile"
                        name="mobile"
                        type="text"
                        autoComplete="off"
                        value={mobile}
                        onChange={onMobileChanged}
                        maxLength={10}
                        required
                    />
                </Form.Group>
                
                <Form.Group className="formGroup">
                    <Form.Check
                        type="checkbox"
                        id="user-active"
                        label="ACTIVE:"
                        checked={active}
                        onChange={onActiveChanged}
                        className="form__checkbox-container"
                    />
                </Form.Group>

                <Form.Group className="formGroup">
                    <Form.Label htmlFor="roles">
                        ASSIGNED ROLES:
                    </Form.Label>
                    <Form.Control
                        as="select"
                        multiple
                        id="roles"
                        name="roles"
                        className={`${validRolesClass}`}
                        size="3"
                        value={roles}
                        onChange={onRolesChanged}
                        required
                    >
                        {rolesOptions}
                    </Form.Control>
                </Form.Group>

                <Form.Group className="formGroup">
                    <Form.Label htmlFor="membershipTier">
                        MEMBERSHIP TIER:
                    </Form.Label>
                    <Form.Control
                        as="select"
                        id="membershipTier"
                        name="membershipTier"
                        value={membershipTier}
                        onChange={onMembershipTierChanged}
                        required
                    >
                        {membershipTierOptions}
                    </Form.Control>
                </Form.Group>

                <Form.Group className="formGroup">
                    <Form.Label htmlFor="membershipStatus">
                        MEMBERSHIP STATUS:
                    </Form.Label>
                    <Form.Control
                        as="select"
                        id="membershipStatus"
                        name="membershipStatus"
                        value={membershipStatus}
                        onChange={onMembershipStatusChanged}
                        required
                    >
                        {membershipStatusOptions}
                    </Form.Control>
                </Form.Group>

                <Form.Group className="formGroup">
                    <Form.Check
                        type="checkbox"
                        id="isSponsoredMember"
                        label="SPONSORED MEMBER:"
                        checked={isSponsoredMember}
                        onChange={onIsSponsoredMemberChanged}
                        className="form__checkbox-container"
                    />
                </Form.Group>

                {/* ✅ नया ब्लॉक फ़ील्ड: isBlocked */}
                <Form.Group className="formGroup">
                    <Form.Check
                        type="checkbox"
                        id="isBlocked"
                        label="BLOCK USER:"
                        checked={isBlocked}
                        onChange={onIsBlockedChanged}
                        className="form__checkbox-container"
                    />
                </Form.Group>

                {/* ✅ यदि उपयोगकर्ता ब्लॉक है तो blockedReason दिखाएं */}
                {isBlocked && (
                    <Form.Group className="formGroup">
                        <Form.Label htmlFor="blockedReason">
                            BLOCK REASON:
                        </Form.Label>
                        <Form.Control
                            className={`${validBlockedReasonClass}`} // ✅ नई क्लास
                            id="blockedReason"
                            name="blockedReason"
                            type="text"
                            value={blockedReason}
                            onChange={onBlockedReasonChanged}
                            required={isBlocked} // ✅ यदि ब्लॉक किया गया है तो कारण अनिवार्य है
                        />
                         {isBlockedReasonRequired && <p className="errmsg">ब्लॉक का कारण अनिवार्य है।</p>} {/* ✅ त्रुटि संदेश */}
                    </Form.Group>
                )}

            </Form>
            </div>
        </div>
        </>
    )

    return content
}
export default EditUserForm
